import { observable, runInAction } from "mobx"

import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectSecretsQueryKey } from "@/lib/queries.ts"

import type { ProjectKey, ProjectSecret } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"

const log = getLogger("ProjectSecretsViewModel")

export class ProjectSecretsViewModel extends Model {
  readonly secrets = observable.box<ProjectSecret[]>([])
  readonly error = observable.box<string | null>(null)

  constructor(
    readonly context: OrgContext,
    private readonly projectKey: ProjectKey,
    private readonly taskId?: string
  ) {
    super("ProjectSecretsViewModel")

    void this.load()
  }

  async listSecrets() {
    if (this.taskId) {
      return (await withGlobalLoader(this.context, this.context.queries.taskDetails(this.taskId))).task.initRuntimeConfigState?.secrets?.filter(it => !it.skipped) ?? []
    } else {
      return await withGlobalLoader(this.context, this.context.queries.listProjectSecrets(this.projectKey))
    }
  }

  async add(newSecret: ProjectSecret, value: string) {
    if (this.taskId) {
      await withGlobalLoader(this.context, this.context.queries.apiClient.updateSecretInSetupTask(this.taskId, {
        ...newSecret,
        value: btoa(value),
        changed: true
      }))
    } else {
      await withGlobalLoader(this.context, this.context.queries.apiClient.addProjectSecret(this.projectKey, newSecret, btoa(value)))
    }
  }

  async update(secret: ProjectSecret, value: string) {
    if (this.taskId) {
      await withGlobalLoader(this.context, this.context.queries.apiClient.updateSecretInSetupTask(this.taskId, {
        ...secret,
        value: btoa(value),
        changed: true
      }))
    } else {
      await withGlobalLoader(this.context, this.context.queries.apiClient.updateProjectSecret(this.projectKey, secret.key, secret.description, btoa(value)))
    }
  }

  async delete(key: string) {
    if (this.taskId) {
      await withGlobalLoader(this.context, this.context.queries.apiClient.deleteSecretInSetupTask(this.taskId, key))
    } else {
      await withGlobalLoader(this.context, this.context.queries.apiClient.deleteProjectSecret(this.projectKey, key))
    }
  }

  async load() {
    this.isLoading.set(true)
    this.error.set(null)

    try {
      const secrets = await this.listSecrets()
      runInAction(() => {
        this.secrets.set(secrets)
        this.isLoading.set(false)
      })
    } catch (error) {
      runInAction(() => {
        this.error.set(`Failed to load project secrets: ${error}`)
        this.isLoading.set(false)
      })
      log.error("Failed to load project secrets:", error)
    }
  }

  readonly addSecret = async (key: string, description: string, value: string) => {
    const newSecret: ProjectSecret = {
      key: key,
      description: description || null,
      skipped: false
    }

    try {
      await this.add(newSecret, value)
      void this.context.queryClient.invalidateQueries({ queryKey: projectSecretsQueryKey(this.projectKey) })
      runInAction(() => this.secrets.set([...this.secrets.get(), newSecret]))
    } catch (error) {
      runInAction(() => this.error.set(`Failed to add secret: ${error}`))
      log.error("Failed to add project secret:", error)
      throw error
    }
  }

  readonly deleteSecret = async (key: string) => {
    try {
      await this.delete(key)
      void this.context.queryClient.invalidateQueries({ queryKey: projectSecretsQueryKey(this.projectKey) })
      runInAction(() => this.secrets.set(this.secrets.get().filter(secret => secret.key !== key)))
    } catch (error) {
      runInAction(() => this.error.set(`Failed to delete secret: ${error}`))
      log.error("Failed to delete project secret:", error)
      throw error
    }
  }

  readonly updateSecretValue = async (key: string, value: string) => {
    try {
      const description = this.secrets.get().find(it => it.key === key)?.description ?? ""
      await this.update({ key, description, skipped: false }, value)
      void this.context.queryClient.invalidateQueries({ queryKey: projectSecretsQueryKey(this.projectKey) })
    } catch (error) {
      runInAction(() => this.error.set(`Failed to update secret value: ${error}`))
      log.error("Failed to update project secret value:", error)
      throw error
    }
  }
}
